import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Paper,
  Skeleton,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Layout from "../components/layout/Layout";
import { useBreadcrumb } from "../context/BreadcrumbContext";
import {
  getPortalWideLpe1Section12Defaults,
  updatePortalWideLpe1Section12Defaults,
} from "../api/propertyApi";
import { getLPE1FormFieldSchema } from "../api/lpe1Api";
import EstateCodeSettingsPage from "./EstateCodeSettingsPage";

const MANAGING_AGENT_FIELD_KEYS = [
  "1.3 Name",
  "1.3 Address",
  "1.3 Telephone",
  "1.3 Email",
  "1.3 SC",
  "1.3 Account Name",
  "1.3 Account number",
  "1.3 Reference",
  "1.3 Fee Type",
];

const SIMPLE_LABELS = {
  "1.3 Name": "Name",
  "1.3 Address": "Address",
  "1.3 Telephone": "Telephone",
  "1.3 Email": "Email",
  "1.3 SC": "Sort Code",
  "1.3 Account Name": "Account Name",
  "1.3 Account number": "Account number",
  "1.3 Reference": "Reference",
  "1.3 Fee Type": "Fee Type",
};

export default function PortalWideDefaultsPage({ embedded = false, showEstateCodeSettings = false }) {
  const navigate = useNavigate();
  const { setCustomPath, clearCustomPath } = useBreadcrumb();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formFieldSchema, setFormFieldSchema] = useState([]);
  const [formValues, setFormValues] = useState({});
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  useEffect(() => {
    if (embedded) return undefined;
    setCustomPath([
      { label: "Settings", path: "/settings" },
      { label: "Company Information", path: null },
    ]);
    return () => clearCustomPath();
  }, [setCustomPath, clearCustomPath, embedded]);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        setLoading(true);
        const [portalDefaults, fields] = await Promise.all([
          getPortalWideLpe1Section12Defaults(),
          getLPE1FormFieldSchema(),
        ]);
        if (!active) return;
        setFormValues(portalDefaults || {});
        setFormFieldSchema(fields || []);
      } catch (error) {
        console.error("Failed to load company settings:", error);
        if (active) {
          setToast({
            open: true,
            message: "Failed to load company settings",
            severity: "error",
          });
        }
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, []);

  const labelsByKey = useMemo(() => {
    const next = {};
    formFieldSchema.forEach((item) => {
      next[item.field_key] = item.question;
    });
    return next;
  }, [formFieldSchema]);

  const fieldLabelFor = (fieldKey) =>
    SIMPLE_LABELS[fieldKey] || labelsByKey[fieldKey] || fieldKey;

  const handleSave = async () => {
    try {
      setSaving(true);
      const payload = {};
      MANAGING_AGENT_FIELD_KEYS.forEach((key) => {
        payload[key] = formValues?.[key] ?? "";
      });
      const response = await updatePortalWideLpe1Section12Defaults(payload);
      setFormValues(response?.data?.metadata || payload);
      setToast({
        open: true,
        message: "company settings updated",
        severity: "success",
      });
    } catch (error) {
      console.error("Failed to save company settings:", error);
      setToast({
        open: true,
        message: "Failed to update company settings",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const content = (
    <Box sx={embedded ? {} : { p: 3 }}>
      {!embedded && (
        <Box sx={{ mb: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="h5" fontWeight={600}>
            Company Information
          </Typography>
          <Button variant="text" onClick={() => navigate(-1)}>
            Back
          </Button>
        </Box>
      )}

      {loading ? (
        <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
          <Skeleton variant="text" width="45%" />
          <Skeleton variant="rectangular" height={240} sx={{ mt: 2 }} />
        </Paper>
      ) : (
        <Stack spacing={2}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              These global defaults are used throughout the portal where applicable.
            </Typography>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Managing Agent
            </Typography>
            <Stack spacing={2}>
              {MANAGING_AGENT_FIELD_KEYS.map((key) => (
                <TextField
                  key={key}
                  fullWidth
                  label={fieldLabelFor(key)}
                  value={formValues?.[key] ?? ""}
                  onChange={(e) =>
                    setFormValues((prev) => ({
                      ...prev,
                      [key]: e.target.value,
                    }))
                  }
                />
              ))}
            </Stack>
          </Paper>

          {showEstateCodeSettings && (
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                General
              </Typography>
              <EstateCodeSettingsPage />
            </Paper>
          )}

          <Button sx={{ alignSelf: "flex-start" }} variant="contained" disabled={saving} onClick={handleSave}>
            Save Information
          </Button>
        </Stack>
      )}
    </Box>
  );

  const toastNode = (
    <Snackbar
      open={toast.open}
      autoHideDuration={3000}
      onClose={() => setToast((prev) => ({ ...prev, open: false }))}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
    >
      <Alert severity={toast.severity} variant="filled" onClose={() => setToast((prev) => ({ ...prev, open: false }))}>
        {toast.message}
      </Alert>
    </Snackbar>
  );

  if (embedded) {
    return (
      <>
        {content}
        {toastNode}
      </>
    );
  }

  return (
    <Layout>
      {content}
      {toastNode}
    </Layout>
  );
}
